<template>
  <div class="vouchers-table-container">
    <!-- Desktop Table View -->
    <table v-if="!isMobileView" class="vouchers-table">
      <thead>
        <tr>
          <th class="expand-col"></th>
          <th class="voucher-col">Voucher #</th>
          <th class="date-col">Date</th>
          <th class="narration-col">Narration</th>
          <th class="amount-col">Debit</th>
          <th class="amount-col">Credit</th>
          <th class="status-col">Status</th>
        </tr>
      </thead>

      <!-- v-for directly on tbody -->
      <tbody v-for="voucher in journalVouchers" :key="voucher.id">
        <!-- Main Row -->
        <tr class="voucher-row" @click="toggleExpand(voucher.id)">
          <td class="expand-col">
            <span
              class="expand-icon"
              :class="{ expanded: expandedVoucher === voucher.id }"
            >
              &#x25B6;
            </span>
          </td>
          <td class="voucher-col">{{ voucher.voucherNumber }}</td>
          <td class="date-col">{{ formatDate(voucher.voucherDate) }}</td>
          <td class="narration-col">{{ voucher.description }}</td>
          <td class="amount-col">{{ calculateTotalDebit(voucher.journalEntries) }}</td>
          <td class="amount-col">{{ calculateTotalCredit(voucher.journalEntries) }}</td>
          <td class="status-col">
            <span class="status-badge">posted</span>
          </td>
        </tr>

        <!-- Expanded Details Row (same voucher, directly below) -->
        <tr v-if="expandedVoucher === voucher.id" class="details-row">
          <td colspan="7">
            <div class="details-container">
              <h4>Journal Entries</h4>

              <table class="entries-table">
                <thead>
                  <tr>
                    <th>Account Code</th>
                    <th>Account Name</th>
                    <th>Party Name</th>
                    <th>Party Email</th>
                    <th>Debit</th>
                    <th>Credit</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="entry in voucher.journalEntries"
                    :key="entry.id"
                    class="entry-row"
                  >
                    <td>{{ entry.chartOfAccount.code }}</td>
                    <td>{{ entry.chartOfAccount.name }}</td>
                    <td>{{ entry.party?.name || '-' }}</td>
                    <td>{{ entry.party?.email || '-' }}</td>
                    <td class="debit-cell">${{ entry.debit.toLocaleString() }}</td>
                    <td class="credit-cell">${{ entry.credit.toLocaleString() }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Mobile Card View -->
    <div v-else class="mobile-cards">
      <div
        v-for="voucher in journalVouchers"
        :key="voucher.id"
        class="voucher-card"
        @click="toggleExpand(voucher.id)"
      >
        <div class="card-header">
          <div class="card-header-left">
            <span class="voucher-number">{{ voucher.voucherNumber }}</span>
            <span class="status-badge">posted</span>
          </div>
          <span
            class="expand-icon"
            :class="{ expanded: expandedVoucher === voucher.id }"
          >
            &#x25B6;
          </span>
        </div>

        <div class="card-body">
          <div class="card-row">
            <span class="card-label">Date</span>
            <span class="card-value">{{ formatDate(voucher.voucherDate) }}</span>
          </div>
          <div class="card-row">
            <span class="card-label">Narration</span>
            <span class="card-value narration">{{ voucher.description }}</span>
          </div>
          <div class="card-row amounts">
            <div class="amount-item">
              <span class="card-label">Debit</span>
              <span class="card-value debit">{{ calculateTotalDebit(voucher.journalEntries) }}</span>
            </div>
            <div class="amount-item">
              <span class="card-label">Credit</span>
              <span class="card-value credit">{{ calculateTotalCredit(voucher.journalEntries) }}</span>
            </div>
          </div>
        </div>

        <!-- Expanded Entries -->
        <div v-if="expandedVoucher === voucher.id" class="card-entries">
          <h4>Journal Entries</h4>
          <div
            v-for="entry in voucher.journalEntries"
            :key="entry.id"
            class="entry-card"
          >
            <div class="entry-row">
              <span class="entry-label">Account</span>
              <span class="entry-value">{{ entry.chartOfAccount.code }} - {{ entry.chartOfAccount.name }}</span>
            </div>
            <div class="entry-row" v-if="entry.party">
              <span class="entry-label">Party</span>
              <span class="entry-value">{{ entry.party.name }}</span>
            </div>
            <div class="entry-amounts">
              <div class="entry-amount">
                <span class="entry-label">Debit</span>
                <span class="entry-value debit">${{ entry.debit.toLocaleString() }}</span>
              </div>
              <div class="entry-amount">
                <span class="entry-label">Credit</span>
                <span class="entry-value credit">${{ entry.credit.toLocaleString() }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'JournalVouchersTable',

  data() {
    return {
      expandedVoucher: null,
      journalVouchers: [],
      windowWidth: window.innerWidth
    }
  },

  computed: {
    isMobileView() {
      return this.windowWidth < 768
    }
  },

  methods: {
    ...mapActions('accounting', ['fetchJournalVouchers']),

    toggleExpand(id) {
      this.expandedVoucher = this.expandedVoucher === id ? null : id
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },

    calculateTotalDebit(entries) {
      const total = entries.reduce((s, e) => s + (e.debit || 0), 0)
      return `$${total.toLocaleString()}`
    },

    calculateTotalCredit(entries) {
      const total = entries.reduce((s, e) => s + (e.credit || 0), 0)
      return `$${total.toLocaleString()}`
    },

    async getJournalVouchers() {
      this.journalVouchers = await this.fetchJournalVouchers()
    },

    handleResize() {
      this.windowWidth = window.innerWidth
    }
  },

  created() {
    this.getJournalVouchers()
  },

  mounted() {
    window.addEventListener('resize', this.handleResize)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  }
}
</script>

<style scoped>
.vouchers-table-container {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.vouchers-table {
  width: 100%;
  border-collapse: collapse;
}

.vouchers-table thead {
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.vouchers-table th {
  padding: 14px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  background: #f9fafb;
}

.voucher-row {
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  transition: background 0.2s ease;
}

.voucher-row:hover {
  background: #f3f4f6;
}

.vouchers-table td {
  padding: 14px 16px;
  font-size: 13px;
  color: #1f2937;
}

.expand-col {
  width: 40px;
  text-align: center;
}

.expand-icon {
  display: inline-block;
  transition: transform 0.2s ease;
  font-size: 12px;
  color: #9ca3af;
}

.expand-icon.expanded {
  transform: rotate(90deg);
}

.voucher-col {
  font-weight: 600;
  color: #2563eb;
}

.date-col {
  width: 120px;
  color: #6b7280;
}

.narration-col {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.amount-col {
  text-align: center;
  width: 90px;
  font-weight: 600;
}

.status-col {
  text-align: center;
  width: 100px;
}

.status-badge {
  background: #2563eb;
  color: white;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

/* Details Row */
.details-row td {
  padding: 20px 16px;
  background: #f9fafb;
}

.details-container {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.entries-table {
  width: 100%;
  border-collapse: collapse;
}

.entries-table thead {
  background: #f3f4f6;
}

.entries-table th {
  padding: 12px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  border-bottom: 1px solid #e5e7eb;
}

.entry-row {
  border-bottom: 1px solid #e5e7eb;
}

.entry-row:hover {
  background: #f9fafb;
}

.entries-table td {
  padding: 12px;
  font-size: 13px;
  color: #4b5563;
}

.debit-cell {
  color: #059669;
  font-weight: 600;
}

.credit-cell {
  color: #dc2626;
  font-weight: 600;
}

/* Mobile Card View */
.mobile-cards {
  padding: 16px;
}

.voucher-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  margin-bottom: 16px;
  overflow: hidden;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.card-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.voucher-number {
  font-weight: 600;
  color: #2563eb;
  font-size: 14px;
}

.card-body {
  padding: 16px;
}

.card-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f6;
}

.card-row:last-child {
  border-bottom: none;
}

.card-label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

.card-value {
  font-size: 14px;
  color: #1f2937;
  text-align: right;
}

.card-value.narration {
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-row.amounts {
  display: flex;
  gap: 24px;
  padding-top: 12px;
}

.amount-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.card-value.debit {
  color: #059669;
  font-weight: 600;
}

.card-value.credit {
  color: #dc2626;
  font-weight: 600;
}

/* Mobile Expanded Entries */
.card-entries {
  background: #f9fafb;
  padding: 16px;
  border-top: 1px solid #e5e7eb;
}

.card-entries h4 {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 12px;
}

.entry-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
}

.entry-card:last-child {
  margin-bottom: 0;
}

.entry-card .entry-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
}

.entry-label {
  font-size: 12px;
  color: #6b7280;
}

.entry-value {
  font-size: 13px;
  color: #1f2937;
}

.entry-amounts {
  display: flex;
  gap: 16px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f3f4f6;
}

.entry-amount {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.entry-value.debit {
  color: #059669;
  font-weight: 600;
}

.entry-value.credit {
  color: #dc2626;
  font-weight: 600;
}
</style>
